import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * Order-level unit limit — storefront UX layer.
 *
 * Caps the TOTAL number of units in a single order. The authoritative check is
 * the `order-unit-limit` cart & checkout validation function; this module only
 * exists so customers hit the limit somewhere they can act on it instead of
 * being bounced at checkout.
 *
 * Three surfaces:
 *   1. Cart page  — server-rendered by snippets/order-unit-limit.liquid. This
 *                   module only clamps the quantity steppers there.
 *   2. Rebuy Smart Cart flyout — the live drawer (settings.cart_type is 'page',
 *                   so the theme's own drawer never renders). Patched here.
 *   3. PDP        — clamps the add-to-cart stepper so people don't add units
 *                   they'll only have to remove again.
 *
 * Liquid does all the counting — see snippets/order-unit-limit.liquid. This module
 * never counts the cart itself, because free-gift lines are identified by product
 * tag and product tags are invisible to /cart.js.
 *
 * State comes from three places, in order of preference:
 *   - `data-unit-limit-*` attributes on the cart section. Exact, and re-rendered
 *     by Liquid on every theme-driven cart mutation via section morphing. NOT
 *     refreshed by Rebuy mutations, which bypass the theme entirely — so any
 *     rebuy:* event forces the section fetch below.
 *   - The page-level JSON blob. Accurate at page load only. On templates other
 *     than product/cart it carries the cap but no unit count, because walking
 *     `item.product.tags` for every cart line on every page request is a real
 *     cost we don't want on collection or home pages.
 *   - `/cart?sections=order-unit-limit`, which re-renders the same Liquid on
 *     demand. Exact, and the only path that's correct after a Rebuy mutation.
 */

const DATA_ELEMENT_ID = 'order-unit-limit-data';

/** Section registered in templates/cart.json purely as a count endpoint. */
const SECTION_ID = 'order-unit-limit';

const REBUY_FLYOUT = '.rebuy-cart__flyout';

/**
 * Line-item anchors inside the drawer, used to fingerprint the cart's contents.
 * Both are classes the theme already styles in assets/rebuy.css, and
 * `-quantity` is the one rebuy-gwp-hide-quantity.js reaches into — so they're
 * confirmed against the live DOM rather than guessed, the same caution the
 * REBUY_CHECKOUT_CONTAINER note below records.
 */
const REBUY_ITEM_REMOVE = '.rebuy-cart__flyout-item-remove';
const REBUY_ITEM_QUANTITY = '.rebuy-cart__flyout-item-quantity';

const NOTICE_CLASS = 'bl101-unit-limit-notice';
const NOTICE_LINK_CLASS = 'bl101-unit-limit-notice__link';
const BLOCKED_CLASS = 'bl101-unit-limit-blocked';

/**
 * Marks controls this module disabled, so we only ever re-enable our own work
 * and never clobber a control Rebuy disabled for its own reasons.
 */
const OWNED_ATTRIBUTE = 'data-bl101-unit-limit-disabled';

/**
 * Rebuy renders both of its checkout buttons inside this container. Confirmed
 * against the live DOM — earlier revisions guessed at class names, and one of
 * those guesses (`[class*="checkout"]`) matched the whole footer, which via the
 * capture-phase click guard below swallowed clicks on the quantity and remove
 * controls and trapped the customer in the over-limit state.
 *
 * Keying off the container is what keeps this safe: the controls a customer needs
 * in order to get back under the limit live outside it.
 */
const REBUY_CHECKOUT_CONTAINER = '[data-target="checkout-buttons-container"]';
const REBUY_CHECKOUT_SELECTOR = `${REBUY_CHECKOUT_CONTAINER} button`;

/** The two subtrees whose contents this module cares about. */
const REBUY_ROOTS = `${REBUY_FLYOUT}, ${REBUY_CHECKOUT_CONTAINER}`;

/**
 * The whole checkout area. Blocked via CSS rather than JS: Rebuy renders and
 * re-renders this at unpredictable times, so any approach that depends on the
 * element existing at the moment our code runs will intermittently do nothing.
 * A class on <html> is timing-independent — the same trick cart-continuity.js
 * uses with html.bl101-iab and the early-drop gate uses with html.edc-active.
 */
const REBUY_CHECKOUT_AREA = '[data-rebuy-component="checkout-area"]';

/** Toggled on <html>. Styling lives in assets/rebuy.css. */
const EXCEEDED_CLASS = 'bl101-unit-limit-exceeded';

/**
 * Rebuy fires a generic cart.change for some interactions but only the specific
 * line-item events for others. Both sets are needed — see the same list in
 * assets/free-gift.js and sections/bts-sale.liquid.
 */
const REBUY_REFRESH_EVENTS = [
  'rebuy:cart.change',
  'rebuy:smartcart.ready',
  'rebuy:smartcart.show',
  'rebuy:smartcart.line-item-increase',
  'rebuy:smartcart.line-item-decrease',
  'rebuy:smartcart.line-item-removed',
  'rebuy.add',
  'rebuy.change',
];

/** Theme-driven cart mutations. These do re-render the cart section. */
const THEME_REFRESH_EVENT = 'cart:update';

/** @typedef {{units: number, cap: number, remaining: number, over: boolean}} UnitLimitState */

/** @type {UnitLimitState} */
const state = {
  units: 0,
  // Infinity until we know better, so an unconfigured store behaves as if there
  // were no limit rather than blocking every cart.
  cap: Infinity,
  remaining: Infinity,
  over: false,
};

/**
 * Debug surface, mirroring window.BL101CartContinuity in cart-continuity.js.
 * In the console: BL101UnitLimit.report()
 */
const debug = {
  /** @type {string} Where the current numbers came from. */
  source: 'init',
  /** @type {string | null} */
  lastError: null,
  /** @type {string[]} Refresh triggers actually observed. Capped at 50. */
  events: [],
  /** @type {string | null} What the cart MutationObserver is attached to, if anything. */
  observerRoot: null,
  /** @type {number} Mutation batches that were dropped as cart-irrelevant. */
  mutationsIgnored: 0,
};

/** Set by readState so report() can show what Liquid last said. */
const lastRead = {
  /** @type {unknown} */ cap: null,
  /** @type {unknown} */ units: null,
};

/**
 * @returns {UnitLimitState} A copy of the current state.
 */
export function getOrderUnitLimitState() {
  return { ...state };
}

/**
 * Clamps a quantity selector's effective max by the order's remaining headroom.
 *
 * Called from both QuantitySelectorComponent (PDP: "how many to add") and
 * CartQuantitySelectorComponent (cart: "what should this line total be"), which
 * is why the two cases are handled separately.
 *
 * @param {QuantitySelectorComponent} selector
 * @param {number | null} base - The effective max the selector would use on its own.
 * @returns {number | null}
 */
export function clampToOrderUnitLimit(selector, base) {
  if (!Number.isFinite(state.remaining)) return base;

  const { min, cartQuantity } = selector.getCurrentValues();
  const isCartLine = Boolean(selector.refs.quantityInput?.dataset.cartLine);

  // On a cart line the input value IS the line total, so the line may grow by
  // whatever headroom is left. We deliberately use the server-rendered
  // cartQuantity rather than the live input value — the live value climbs as the
  // customer clicks +, which would let the ceiling climb along with it.
  //
  // Floored at min because a stepper that can't reach its own minimum is a
  // broken control. The consequence is that with zero headroom a PDP add of
  // `min` still goes through; the cart page then shows the over-limit state.
  const allowance = Math.max(isCartLine ? cartQuantity + state.remaining : state.remaining, min);

  return base === null ? allowance : Math.min(base, allowance);
}

/**
 * @param {{cap?: unknown, units?: unknown}} source
 * @returns {'full' | 'cap' | false} How much of the state was resolved.
 */
function readState(source) {
  lastRead.cap = source.cap;
  lastRead.units = source.units;

  const cap = Number(source.cap);
  if (!Number.isFinite(cap) || cap <= 0) return false;

  state.cap = cap;

  const units = Number(source.units);
  if (!Number.isFinite(units) || units < 0) return 'cap';

  state.units = units;
  state.remaining = Math.max(cap - units, 0);
  state.over = units > cap;

  return 'full';
}

/**
 * Reads the data-* attributes the cart section renders.
 * @returns {'full' | 'cap' | false}
 */
function readFromCartAttrs() {
  const host = document.querySelector('[data-unit-limit-cap]');
  if (!(host instanceof HTMLElement)) return false;

  return readState({ cap: host.dataset.unitLimitCap, units: host.dataset.unitLimitUnits });
}

/**
 * Reads the page-level JSON blob. Accurate at page load only — nothing
 * re-renders it — so this is for initialisation, never for refresh.
 * @returns {'full' | 'cap' | false}
 */
function readFromJsonBlob() {
  const blob = document.getElementById(DATA_ELEMENT_ID);
  if (!blob?.textContent) return false;

  try {
    return readState(JSON.parse(blob.textContent));
  } catch {
    return false;
  }
}

/**
 * Asks Liquid for an exact count via the Section Rendering API.
 *
 * This deliberately does NOT use /cart.js. BL101's Rebuy gift-with-purchase
 * lines are identified by the `gwp`/`hidden` product tags and carry no `_gwp`
 * line item property (verified on order #1987491), and the AJAX cart API doesn't
 * expose product tags — so any client-side count includes free gifts and reports
 * the customer over the limit when they aren't.
 *
 * @returns {Promise<void>}
 */
async function readFromCartSection() {
  // Cheap path: render just the one section.
  if (await readAttrsFrom(`${Theme.routes.cart_url}?sections=${SECTION_ID}`, SECTION_ID)) {
    debug.source = 'section';
    return;
  }

  // Guaranteed path: the cart page is a plain document with the same attributes
  // on .cart__ctas, so it can't depend on Section Rendering API behaviour.
  // Heavier, hence second.
  if (await readAttrsFrom(Theme.routes.cart_url)) {
    debug.source = 'cart-page';
    console.warn(
      '[unit-limit] section fetch returned nothing; fell back to fetching /cart. ' +
        'Check that sections/order-unit-limit.liquid is registered in templates/cart.json.'
    );
    return;
  }

  debug.source = 'none';
  console.warn('[unit-limit] could not read cart state; storefront limit UI is inactive.');
}

/**
 * Fetches a URL and reads the unit-limit data attributes out of it.
 * @param {string} url
 * @param {string} [sectionKey] - When set, treat the response as Section Rendering JSON.
 * @returns {Promise<boolean>} Whether full state was resolved.
 */
async function readAttrsFrom(url, sectionKey) {
  try {
    const response = await fetch(url, {
      headers: { Accept: sectionKey ? 'application/json' : 'text/html' },
    });

    if (!response.ok) {
      debug.lastError = `${url} -> HTTP ${response.status}`;
      return false;
    }

    let html;

    if (sectionKey) {
      const sections = await response.json();
      html = sections[sectionKey];
      if (typeof html !== 'string') {
        debug.lastError = `${url} -> no "${sectionKey}" key (got: ${Object.keys(sections).join(', ') || 'nothing'})`;
        return false;
      }
    } else {
      html = await response.text();
    }

    const host = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('[data-unit-limit-cap]');

    if (!(host instanceof HTMLElement)) {
      debug.lastError = `${url} -> no [data-unit-limit-cap] in response`;
      return false;
    }

    return (
      readState({ cap: host.dataset.unitLimitCap, units: host.dataset.unitLimitUnits }) === 'full'
    );
  } catch (error) {
    // Non-fatal: the validation function is still the real guard.
    debug.lastError = `${url} -> ${error instanceof Error ? error.message : String(error)}`;
    return false;
  }
}

function updateQuantitySelectors() {
  for (const selector of document.querySelectorAll(
    'quantity-selector-component, cart-quantity-selector-component'
  )) {
    if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
      selector.updateButtonStates();
    }
  }
}

/**
 * @returns {string}
 */
function unitLimitMessage() {
  const template =
    Theme.translations?.cart_unit_limit_error ||
    'Orders are limited to %{cap} items. You have %{units} — please remove %{excess} to check out.';

  return template
    .replaceAll('%{cap}', String(state.cap))
    .replaceAll('%{units}', String(state.units))
    .replaceAll('%{excess}', String(Math.max(state.units - state.cap, 0)));
}

/**
 * Fills the notice with the count sentence plus a mailto link to support.
 *
 * Built as DOM nodes rather than innerHTML: the strings come from the locale
 * file, which is editable in the admin language editor, so they're not markup we
 * control. Splitting on the %{email} token keeps the sentence translatable —
 * translators can move the address within the sentence.
 *
 * @param {Element} notice
 */
function renderNoticeContent(notice) {
  notice.textContent = '';
  notice.append(`${unitLimitMessage()} `);

  const email = Theme.translations?.support_email;
  const supportTemplate = Theme.translations?.cart_unit_limit_support;
  if (!email || !supportTemplate) return;

  const [before, after = ''] = supportTemplate.split('%{email}');

  const link = document.createElement('a');
  link.className = NOTICE_LINK_CLASS;
  link.href = `mailto:${email}`;
  link.textContent = email;

  notice.append(before, link, after);
}

/**
 * Disables both Rebuy checkout buttons and shows the reason. The cart page is
 * handled entirely in Liquid, so this is drawer-only.
 */
function updateRebuyFlyout() {
  const container = document.querySelector(REBUY_CHECKOUT_CONTAINER);
  const flyout = document.querySelector(REBUY_FLYOUT);

  // The button container is rendered lazily and re-rendered often, so treat it as
  // optional. An earlier revision returned early when it was missing, which meant
  // a single unlucky moment left the drawer with no message AND no disabled
  // buttons — the notice must not depend on the buttons being present.
  if (container instanceof HTMLElement) {
    for (const control of container.querySelectorAll('button')) {
      if (state.over) {
        control.setAttribute(OWNED_ATTRIBUTE, 'true');
        control.setAttribute('aria-disabled', 'true');
        control.classList.add(BLOCKED_CLASS);
        control.disabled = true;
      } else if (control.hasAttribute(OWNED_ATTRIBUTE)) {
        // Only undo our own work — Rebuy disables these buttons for its own
        // reasons too (in-flight requests, empty cart).
        control.removeAttribute(OWNED_ATTRIBUTE);
        control.removeAttribute('aria-disabled');
        control.classList.remove(BLOCKED_CLASS);
        control.disabled = false;
      }
    }
  }

  // Drop any previous notice and re-place it. Cheaper to reason about than
  // tracking whether it's still adjacent to a container that may have appeared,
  // moved or been re-rendered since. Safe from feedback loops because the
  // MutationObserver in init() ignores notice-only mutations.
  document.querySelectorAll(`.${NOTICE_CLASS}`).forEach((node) => node.remove());

  if (!state.over) return;

  // Anchor preference: above the buttons, else above the whole checkout area,
  // else anywhere in the flyout. Anywhere beats nowhere.
  const anchor = container ?? document.querySelector(REBUY_CHECKOUT_AREA);
  const host = anchor?.parentElement ?? flyout;

  if (!(host instanceof HTMLElement)) {
    debug.lastError =
      'over limit, but no checkout container, checkout area or Rebuy flyout is in the DOM';
    return;
  }

  const notice = document.createElement('div');
  notice.className = NOTICE_CLASS;
  notice.setAttribute('role', 'alert');
  renderNoticeContent(notice);

  if (anchor) anchor.before(notice);
  else host.append(notice);
}

/**
 * Rebuy's checkout buttons are rendered by the app, not the theme, so setting
 * `disabled` isn't dependable on its own — Rebuy re-renders the footer and wipes
 * it. Cancelling the interaction is what actually holds. The MutationObserver in
 * init() re-applies the disabled state after each re-render.
 *
 * @param {MouseEvent} event
 */
function guardRebuyCheckoutClick(event) {
  if (!state.over) return;

  const target = event.target;
  if (!(target instanceof Element)) return;
  if (!target.closest(REBUY_CHECKOUT_CONTAINER)) return;

  event.preventDefault();
  event.stopPropagation();
  updateRebuyFlyout();
}

/**
 * Keyboard activation (Tab + Enter) on a submit button fires `submit` without a
 * click, so the guard above never sees it.
 * @param {SubmitEvent} event
 */
function guardRebuyCheckoutSubmit(event) {
  if (!state.over) return;

  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;

  const insideContainer =
    form.closest(REBUY_CHECKOUT_CONTAINER) ??
    (event.submitter instanceof Element ? event.submitter.closest(REBUY_CHECKOUT_CONTAINER) : null);

  if (!insideContainer) return;

  event.preventDefault();
  event.stopPropagation();
  updateRebuyFlyout();
}

function apply() {
  // Do this first and unconditionally. It's the one part that can't be defeated
  // by Rebuy re-rendering, because CSS re-applies itself to whatever appears.
  document.documentElement.classList.toggle(EXCEEDED_CLASS, state.over);

  updateQuantitySelectors();
  updateRebuyFlyout();
}

let refreshHandle = 0;

/**
 * Re-reads state and reapplies.
 *
 * Debounced for two reasons: Rebuy fires several events per mutation, and the
 * theme dispatches `cart:update` *before* it morphs the cart section
 * (component-cart-items.js dispatches, then morphs), so reading the attributes
 * synchronously would see pre-morph values.
 *
 * @param {Event} [event]
 */
function refresh(event) {
  if (event?.type) debug.events = [...debug.events.slice(-49), event.type];

  // Rebuy mutates the cart without re-rendering the theme's cart section, so its
  // data-* attributes are stale after any rebuy:* event. Refetch instead.
  const bypassAttrs = Boolean(event?.type?.startsWith('rebuy'));

  clearTimeout(refreshHandle);

  refreshHandle = setTimeout(
    async () => {
      if (bypassAttrs || readFromCartAttrs() !== 'full') await readFromCartSection();
      apply();
    },
    bypassAttrs ? 200 : 50
  );
}

/** @type {MutationObserver | null} Watches Rebuy's cart. Attached lazily. */
let cartObserver = null;

/** @type {MutationObserver | null} Waits for Rebuy's cart to exist, then hands over. */
let bootstrapObserver = null;

/** @type {Element | null} The node cartObserver is attached to. */
let observedRoot = null;

/** @type {string} Fingerprint of the drawer's line items as of the last check. */
let lastSignature = '';

/**
 * Reads a line's quantity as an opaque token. Rebuy renders the widget as
 * buttons around either an input or a bare text node depending on the line, so
 * both are handled — the value is only ever compared with itself.
 *
 * @param {Element} cell
 * @returns {string}
 */
function readLineQuantity(cell) {
  const input = cell.querySelector('input');
  if (input instanceof HTMLInputElement) return input.value.trim();

  return (cell.textContent ?? '').replace(/\s+/g, '');
}

/**
 * Fingerprints the drawer's line items: how many lines there are, plus each
 * line's quantity. It changes if and only if the cart's unit count can have
 * moved.
 *
 * This is what keeps the observer below from being chatty. Rebuy puts
 * recommendation carousels, countdown tickers and loading spinners inside the
 * flyout, and an earlier revision treated every one of those mutations as a cart
 * change — each costing a `/cart?sections=` request, so a drawer containing any
 * ticker refetched on a 200ms loop for as long as it stayed open.
 *
 * @returns {string}
 */
function lineItemSignature() {
  const flyout = document.querySelector(REBUY_FLYOUT);
  if (!flyout) return '';

  const quantities = [...flyout.querySelectorAll(REBUY_ITEM_QUANTITY)].map(readLineQuantity);

  return `${flyout.querySelectorAll(REBUY_ITEM_REMOVE).length}:${quantities.join('|')}`;
}

/**
 * Safety net for Rebuy's unreliable events — rebuy-gwp-hide-quantity.js keeps a
 * MutationObserver for exactly the same reason. If the flyout's line items change
 * and no event reached us, refresh anyway.
 *
 * @param {MutationRecord[]} mutations
 */
function handleCartMutations(mutations) {
  let touchedCart = false;
  let controlsAppeared = false;

  for (const mutation of mutations) {
    const touched = [...mutation.addedNodes, ...mutation.removedNodes];

    // Ignore our own notice, or apply() -> insert notice -> observer ->
    // apply() loops forever on the debounce interval.
    const ours = touched.every(
      (node) => node instanceof Element && node.classList.contains(NOTICE_CLASS)
    );
    if (touched.length && ours) continue;

    // Rebuy's footer can be re-rendered outside .rebuy-cart__flyout, so a
    // `closest` test on the mutation target alone would miss it appearing.
    // Catch it arriving as an added node too, and just re-apply — no refetch,
    // the numbers haven't changed.
    if (
      touched.some(
        (node) =>
          node instanceof Element &&
          (node.matches?.(REBUY_ROOTS) || node.querySelector?.(REBUY_CHECKOUT_CONTAINER))
      )
    ) {
      controlsAppeared = true;
    }

    if (/** @type {Element} */ (mutation.target).closest?.(REBUY_ROOTS)) touchedCart = true;
  }

  // Refetch only when the lines themselves moved, not for every repaint inside
  // the drawer.
  if (touchedCart) {
    const signature = lineItemSignature();

    if (signature !== lastSignature) {
      lastSignature = signature;
      refresh(new Event('rebuy:observed'));
      return;
    }
  }

  if (controlsAppeared) apply();
  else debug.mutationsIgnored += 1;
}

/**
 * Attaches the cart observer, or re-attaches it if Rebuy has replaced the node it
 * was watching.
 *
 * @returns {boolean} Whether an observer is live.
 */
function observeRebuyCart() {
  const flyout = document.querySelector(REBUY_FLYOUT);
  if (!flyout) return false;

  // The checkout footer is sometimes re-rendered as a sibling of the flyout
  // rather than inside it, so watch Rebuy's own container one level up and let
  // handleCartMutations sort out what matters. Never widen this to <body>: doing
  // so put every lazy image, carousel and third-party widget on the page through
  // the callback, on every page this module loads on.
  const parent = flyout.parentElement;
  const root = parent && parent !== document.body ? parent : flyout;

  if (cartObserver && observedRoot === root && root.isConnected) return true;

  cartObserver?.disconnect();
  cartObserver = new MutationObserver(handleCartMutations);
  cartObserver.observe(root, { childList: true, subtree: true });
  observedRoot = root;
  debug.observerRoot =
    root === flyout
      ? REBUY_FLYOUT
      : `${root.tagName.toLowerCase()}${root.id ? `#${root.id}` : ''} > ${REBUY_FLYOUT}`;

  // Baseline it now, so the first batch of mutations after attaching isn't read
  // as a cart change.
  lastSignature = lineItemSignature();

  bootstrapObserver?.disconnect();
  bootstrapObserver = null;

  return true;
}

/**
 * Backstop for the backstop: if Rebuy fires no events at all AND mounts its cart
 * somewhere other than a direct child of <body>, neither hook in
 * watchForRebuyCart attaches. The drawer can only open off a user interaction, so
 * retry after clicks — one querySelector per click, and the listener removes
 * itself once an observer is live.
 */
function retryAttachAfterClick() {
  if (cartObserver) {
    document.removeEventListener('click', retryAttachAfterClick);
    return;
  }

  // Rebuy renders the drawer in response to the same click, not before it.
  requestAnimationFrame(() => observeRebuyCart());
}

/**
 * Rebuy appends its cart as a direct child of <body> when the app boots, which
 * may be after us and may happen without any rebuy:* event reaching us. This
 * watches for that one arrival — `childList` with no `subtree`, so it only ever
 * sees direct children of <body> rather than the whole page churning — then hands
 * over to observeRebuyCart and disconnects.
 */
function watchForRebuyCart() {
  if (cartObserver || bootstrapObserver) return;

  bootstrapObserver = new MutationObserver(() => observeRebuyCart());
  bootstrapObserver.observe(document.body, { childList: true });
  document.addEventListener('click', retryAttachAfterClick);
}

/**
 * Every Rebuy event is also a chance to attach the cart observer, or re-attach it
 * if Rebuy swapped out its container.
 *
 * @param {Event} event
 */
function handleRebuyEvent(event) {
  observeRebuyCart();
  refresh(event);
}

function init() {
  // Clamp the PDP stepper. CartQuantitySelectorComponent overrides
  // getEffectiveMax, so it opts in separately via clampToOrderUnitLimit.
  const baseGetEffectiveMax = QuantitySelectorComponent.prototype.getEffectiveMax;

  QuantitySelectorComponent.prototype.getEffectiveMax = function getEffectiveMax() {
    return clampToOrderUnitLimit(this, baseGetEffectiveMax.call(this));
  };

  // Cart and product templates render a full count, so there's nothing to fetch.
  // Elsewhere we get the cap alone and deliberately skip the section fetch on
  // load — the count isn't needed until the Rebuy drawer opens, and
  // rebuy:smartcart.show is in the refresh list below.
  if (readFromCartAttrs() !== 'full') readFromJsonBlob();

  document.addEventListener(THEME_REFRESH_EVENT, refresh);
  for (const eventName of REBUY_REFRESH_EVENTS) {
    document.addEventListener(eventName, handleRebuyEvent);
  }
  document.addEventListener('click', guardRebuyCheckoutClick, true);
  document.addEventListener('submit', guardRebuyCheckoutSubmit, true);

  // There's nothing worth observing until Rebuy's drawer exists, and on home and
  // collection pages it may never be opened at all. On the cart page Rebuy is
  // sometimes already up by now; everywhere else this waits for it.
  if (!observeRebuyCart()) watchForRebuyCart();

  Object.assign(window, {
    BL101UnitLimit: {
      state: getOrderUnitLimitState,
      report: () => ({
        ...getOrderUnitLimitState(),
        source: debug.source,
        lastError: debug.lastError,
        rawFromLiquid: { ...lastRead },
        rebuyEventsSeen: [...new Set(debug.events)],
        observerRoot: debug.observerRoot,
        lineItemSignature: lineItemSignature(),
        mutationsIgnored: debug.mutationsIgnored,
        flyoutFound: Boolean(document.querySelector(REBUY_FLYOUT)),
        htmlClassApplied: document.documentElement.classList.contains(EXCEEDED_CLASS),
        checkoutAreaFound: Boolean(document.querySelector(REBUY_CHECKOUT_AREA)),
        checkoutContainerFound: Boolean(document.querySelector(REBUY_CHECKOUT_CONTAINER)),
        checkoutButtonsFound: document.querySelectorAll(REBUY_CHECKOUT_SELECTOR).length,
        checkoutButtonsDisabled: [...document.querySelectorAll(REBUY_CHECKOUT_SELECTOR)].filter(
          (button) => /** @type {HTMLButtonElement} */ (button).disabled
        ).length,
        cartAttrsOnPage: Boolean(document.querySelector('[data-unit-limit-cap]')),
        jsonBlobOnPage: Boolean(document.getElementById(DATA_ELEMENT_ID)),
      }),
      refresh: () => refresh(new Event('rebuy:manual')),
    },
  });

  // Re-run once the patch above is in place: PDP elements upgrade during the
  // import on line 1, so their first updateButtonStates() ran unpatched.
  apply();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
